import { applyTemplateTokens } from './templateTokens';

type PdfPayloadBuildArgs = {
  html: string;
  visualModel?: Record<string, any> | null;
  tokenValues?: Record<string, string>;
  companyLogoUrl?: string | null;
  projectLogoUrl?: string | null;
  companyAddress?: string | null;
  companyMobile?: string | null;
  companyEmail?: string | null;
  companyFontFamily?: string | null;
  companyFontUrl?: string | null;
  companyFontFormat?: string | null;
  gujaratiFontFamily?: string | null;
  gujaratiFontUrl?: string | null;
  gujaratiFontFormat?: string | null;
  paperSize?: string | null;
  fileName: string;
};

const escHtml = (value: string) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatIndianPhone = (value: string) => {
  const raw = String(value || '').trim();
  const digits = raw.replace(/\D/g, '');
  const normalized = digits.length > 10 ? digits.slice(-10) : digits;
  if (!normalized) return raw;
  if (normalized.length <= 4) return `+91 ${normalized}`;
  if (normalized.length <= 7) return `+91 ${normalized.slice(0, 4)} ${normalized.slice(4)}`;
  return `+91 ${normalized.slice(0, 4)} ${normalized.slice(4, 7)} ${normalized.slice(7, 10)}`;
};

const clamp = (value: unknown, min: number, max: number, fallback: number) =>
  Math.max(min, Math.min(max, Number(value ?? fallback) || fallback));

const wrapVisualMainContent = ({
  html,
  vm,
  tokens,
}: {
  html: string;
  vm: Record<string, any>;
  tokens: Record<string, string>;
}) => {
  const source = String(html || '').trim();
  if (!source) return source;
  if (/doc-generic-shell|doc-brand-bg/i.test(source)) return source;

  const showTitle = Boolean(vm.showTitle);
  const showRefNo = Boolean(vm.showRefNo);
  const showDate = Boolean(vm.showDate);
  const title = escHtml(applyTemplateTokens(String(vm.title || ''), tokens));
  const subtitle = escHtml(applyTemplateTokens(String(vm.subtitle || ''), tokens));
  const tokenRefNo = String(tokens['{{ref_no}}'] || '').trim();
  const tokenIssueDate = String(tokens['{{issue_date}}'] || '').trim();
  const resolvedRefNoRaw = tokenRefNo || applyTemplateTokens(String(vm.refNo || '{{ref_no}}'), tokens);
  const resolvedIssueDateRaw = tokenIssueDate || applyTemplateTokens(String(vm.issueDate || '{{issue_date}}'), tokens);
  const refNo = escHtml(String(resolvedRefNoRaw || '').trim());
  const issueDate = escHtml(String(resolvedIssueDateRaw || '').trim());
  const titleAlign = String(vm.titleAlign || 'left').trim() || 'left';
  const backgroundImageUrl = String(vm.backgroundImageUrl || '').trim();
  const backgroundOpacity = Math.max(0, Math.min(0.4, Number(vm.backgroundOpacity || 0) || 0));
  const backgroundScale = Math.max(10, Math.min(300, Number(vm.backgroundScale || 72) || 72));
  const backgroundPosX = clamp(vm.backgroundPosX ?? -44, -300, 300, -44);
  const backgroundPosY = clamp(vm.backgroundPosY ?? 109, -300, 300, 109);
  const backgroundSizeMode = String(vm.backgroundSize || 'contain') === 'cover' ? 'cover' : 'contain';
  const backgroundSizeCss =
    backgroundSizeMode === 'cover' ? 'cover' : `${backgroundScale}% auto`;

  const refDateItems: string[] = [];
  if (showRefNo) refDateItems.push(`<div>Ref No: ${refNo}</div>`);
  if (showDate) refDateItems.push(`<div>Date: ${issueDate}</div>`);

  return [
    `<style>`,
    `.doc-generic-shell{position:relative;min-height:100%;}`,
    `.doc-generic-shell .doc-brand-bg{position:fixed;inset:0;background-repeat:no-repeat;pointer-events:none;z-index:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}`,
    `.doc-generic-shell .doc-generic-main{position:relative;z-index:1;}`,
    `</style>`,
    `<div class="doc-generic-shell">`,
    backgroundImageUrl
      ? `<div class="doc-brand-bg" style="background-image:url('${backgroundImageUrl.replace(/'/g, '%27')}');background-position:${backgroundPosX}% ${backgroundPosY}%;background-size:${backgroundSizeCss};opacity:${backgroundOpacity};"></div>`
      : '',
    `<div class="doc-generic-main">`,
    refDateItems.length
      ? `<div style="display:flex;justify-content:${refDateItems.length > 1 ? 'space-between' : 'flex-start'};align-items:flex-start;gap:8px;margin:0 0 10px;font-size:12px;">${refDateItems.join('')}</div>`
      : '',
    showTitle && title ? `<h1 style="margin:0 0 10px;font-size:22px;text-align:${titleAlign};">${title}</h1>` : '',
    subtitle ? `<p style="margin:0 0 14px;font-weight:600;text-align:${titleAlign};">${subtitle}</p>` : '',
    source,
    `</div>`,
    `</div>`,
  ].join('');
};

export const buildPdfPayloadFromTemplate = ({
  html,
  visualModel,
  tokenValues,
  companyLogoUrl,
  projectLogoUrl,
  companyAddress,
  companyMobile,
  companyEmail,
  companyFontFamily,
  companyFontUrl,
  companyFontFormat,
  gujaratiFontFamily,
  gujaratiFontUrl,
  gujaratiFontFormat,
  paperSize,
  fileName,
}: PdfPayloadBuildArgs) => {
  const vm = visualModel || {};
  const tokens = tokenValues || {};
  const mainContentOffsetMm = clamp(vm.mainContentOffsetCm || 0, -100, 100, 0) * 10;
  const marginTopMm = Math.max(0, Number(vm.marginTopCm || 0) * 10);
  const marginRightMm = Math.max(0, Number(vm.marginRightCm || 0.9) * 10);
  const marginBottomMm = Math.max(0, Number(vm.marginBottomCm || 0) * 10);
  const marginLeftMm = Math.max(0, Number(vm.marginLeftCm || 1.6) * 10);
  const headerHeightMm = 32;
  const footerHeightMm = Math.max(0, Number(vm.footerHeightCm || 0) * 10);
  const showFooterContent = vm.showFooterContent !== false;
  const showPageNumbers = vm.showPageNumbers !== false;
  const footerOverlayEnabled = Boolean(vm.footerOverlayEnabled);
  const footerOverlayRaw = applyTemplateTokens(
    String(vm.footerOverlayText || '').trim(),
    tokens
  );
  const footerOverlayFontSizePx = Math.max(
    8,
    Math.min(72, Number(vm.footerOverlayFontSizePx || 12) || 12)
  );
  const footerOverlayOffsetXMm = clamp(vm.footerOverlayOffsetXCm || 0, -300, 300, 0) * 10;
  const footerOverlayOffsetYMm = clamp(vm.footerOverlayOffsetYCm || 0, -300, 300, 0) * 10;
  const footerOverlayPageScope = vm.footerOverlayPageScope === 'first' ?
    'first' :
    vm.footerOverlayPageScope === 'last' ?
      'last' :
    vm.footerOverlayPageScope === 'custom' ? 'custom' : 'all';
  const footerOverlayPageNumbers = String(vm.footerOverlayPageNumbers || '').trim();
  const companyLogoHeightMm = Math.max(0, Number(vm.companyLogoHeightCm || 0) * 10);
  const projectLogoHeightMm = Math.max(0, Number(vm.projectLogoHeightCm || 0) * 10);
  const companyLogoOffsetXMm = clamp(vm.companyLogoOffsetXCm || 0, -300, 300, 0) * 10;
  const companyLogoOffsetYMm = clamp(vm.companyLogoOffsetYCm || 0, -300, 300, 0) * 10;
  const projectLogoOffsetXMm = clamp(vm.projectLogoOffsetXCm || 0, -300, 300, 0) * 10;
  const projectLogoOffsetYMm = clamp(vm.projectLogoOffsetYCm || 0, -300, 300, 0) * 10;
  const companyHeaderContentType = (
    vm.companyHeaderContentType === 'text' || vm.companyHeaderContentType === 'none'
      ? vm.companyHeaderContentType
      : 'logo'
  ) as 'logo' | 'text' | 'none';
  const projectHeaderContentType = (
    vm.projectHeaderContentType === 'text' || vm.projectHeaderContentType === 'none'
      ? vm.projectHeaderContentType
      : 'logo'
  ) as 'logo' | 'text' | 'none';
  const companyHeaderText = escHtml(
    applyTemplateTokens(String(vm.companyHeaderText || ''), tokens)
  );
  const projectHeaderText = escHtml(
    applyTemplateTokens(String(vm.projectHeaderText || ''), tokens)
  );
  const companyHeaderTextCss = escHtml(
    String(vm.companyHeaderTextCss || 'font-size:12px;line-height:1.2;color:#111;white-space:nowrap;')
  );
  const projectHeaderTextCss = escHtml(
    String(vm.projectHeaderTextCss || 'font-size:12px;line-height:1.2;color:#111;white-space:nowrap;')
  );
  const headerCenterRaw = applyTemplateTokens(String(vm.headerCenterHtml || ''), tokens).trim();
  const headerCenterHtml = headerCenterRaw
    ? (/<[a-z][\s\S]*>/i.test(headerCenterRaw) ? headerCenterRaw : escHtml(headerCenterRaw).replace(/\n/g, '<br/>'))
    : '';

  const effectiveCompanyLogoHeightMm = Math.min(companyLogoHeightMm, headerHeightMm);
  const effectiveProjectLogoHeightMm = Math.min(projectLogoHeightMm, headerHeightMm);
  const companySlotHeightMm = companyHeaderContentType === 'logo' ?
    effectiveCompanyLogoHeightMm :
    Math.max(8, Math.min(headerHeightMm, companyLogoHeightMm || 16));
  const projectSlotHeightMm = projectHeaderContentType === 'logo' ?
    effectiveProjectLogoHeightMm :
    Math.max(8, Math.min(headerHeightMm, projectLogoHeightMm || 16));
  const companySlotTopMm = Math.max(
    0,
    Math.min(
      headerHeightMm - companySlotHeightMm,
      (headerHeightMm - companySlotHeightMm) / 2 + companyLogoOffsetYMm
    )
  );
  const projectSlotTopMm = Math.max(
    0,
    Math.min(
      headerHeightMm - projectSlotHeightMm,
      (headerHeightMm - projectSlotHeightMm) / 2 + projectLogoOffsetYMm
    )
  );
  const printPageTopMarginMm = Math.max(0, marginTopMm + mainContentOffsetMm);
  const printPageBottomMarginMm = Math.max(0, marginBottomMm);

  const parser = new DOMParser().parseFromString(String(html || ''), 'text/html');
  parser.querySelectorAll('.doc-page-header,.doc-page-footer').forEach((node) => node.remove());
  const docBody = parser.querySelector('.doc-body') as HTMLElement | null;
  if (docBody) docBody.style.padding = '0';
  const htmlWithoutOverlay = wrapVisualMainContent({
    html: String(parser.body?.innerHTML || html || ''),
    vm,
    tokens,
  });
  const mapFontFormat = (value: string) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'otf') return 'opentype';
    if (normalized === 'ttf') return 'truetype';
    if (normalized === 'woff' || normalized === 'woff2') return normalized;
    return 'woff2';
  };
  const hasGujaratiText = /[\u0A80-\u0AFF]/.test(String(htmlWithoutOverlay || ''));
  const vmDocFont = String(vm.fontFamily || 'Arial').trim() || 'Arial';
  const letterSpacingPx = Math.max(-2, Math.min(10, Number(vm.letterSpacingPx || 0) || 0));
  const useCompanyFont = Boolean(vm.useCompanyFont);
  const regularFontUrl = String(companyFontUrl || '').trim();
  const regularFontFamily = String(companyFontFamily || '').trim() || (regularFontUrl ? 'CompanyCustomFont' : '');
  const regularFontFormat = mapFontFormat(String(companyFontFormat || ''));
  const gujFontUrl = String(gujaratiFontUrl || '').trim();
  const gujFontFamily = String(gujaratiFontFamily || '').trim() || (gujFontUrl ? 'CompanyGujaratiFont' : '');
  const gujFontFormat = mapFontFormat(String(gujaratiFontFormat || ''));
  const shouldUseGujaratiFont = hasGujaratiText && !!gujFontUrl;
  const effectiveDocFontFamily = shouldUseGujaratiFont
    ? gujFontFamily
    : useCompanyFont && regularFontFamily
      ? regularFontFamily
      : vmDocFont;
  const fontFaces: string[] = [];
  if (regularFontUrl && regularFontFamily) {
    fontFaces.push(
      `@font-face{font-family:'${escHtml(regularFontFamily)}';src:url('${regularFontUrl.replace(/'/g, '%27')}') format('${regularFontFormat}');font-display:swap;}`
    );
  }
  if (gujFontUrl && gujFontFamily) {
    fontFaces.push(
      `@font-face{font-family:'${escHtml(gujFontFamily)}';src:url('${gujFontUrl.replace(/'/g, '%27')}') format('${gujFontFormat}');font-display:swap;}`
    );
  }
  const htmlForPdf = `<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Template Sample PDF</title>
  <style>${fontFaces.join('')}
html, body { margin: 0; padding: 0; background: #fff; font-family: '${escHtml(effectiveDocFontFamily)}', sans-serif; letter-spacing: ${letterSpacingPx}px; }</style>
</head>
<body>${htmlWithoutOverlay}</body>
</html>`;

  const footerAddressRaw = String(companyAddress || '');
  const footerEmailRaw = String(companyEmail || '');
  const footerMobileRaw = String(companyMobile || '');
  const footerAddressText = escHtml(footerAddressRaw);
  const footerEmailText = escHtml(footerEmailRaw);
  const footerPhoneText = escHtml(formatIndianPhone(footerMobileRaw));
  const footerOverlayHasMarkup = /<[a-z][\s\S]*>/i.test(footerOverlayRaw);
  const footerOverlayHtml = footerOverlayHasMarkup
    ? footerOverlayRaw
    : escHtml(footerOverlayRaw).replace(/\n/g, '<br/>');
  const footerOverlayShouldRender = footerOverlayEnabled && !!footerOverlayRaw;
  const pdfHeaderLogoLeftMarginMm = 16;
  const pdfHeaderLogoRightMarginMm = 11;
  const companyHeaderSlotHtml =
    companyHeaderContentType === 'none'
      ? ''
      : companyHeaderContentType === 'text'
        ? `<div style="position:absolute;left:${pdfHeaderLogoLeftMarginMm + companyLogoOffsetXMm}mm;top:${companySlotTopMm}mm;height:${companySlotHeightMm}mm;display:flex;align-items:center;${companyHeaderTextCss}">${companyHeaderText}</div>`
        : `<img src="${String(companyLogoUrl || '')}" style="position:absolute;left:${pdfHeaderLogoLeftMarginMm + companyLogoOffsetXMm}mm;top:${companySlotTopMm}mm;height:${effectiveCompanyLogoHeightMm}mm;object-fit:contain;" />`;
  const projectHeaderSlotHtml =
    projectHeaderContentType === 'none'
      ? ''
      : projectHeaderContentType === 'text'
        ? `<div style="position:absolute;right:${pdfHeaderLogoRightMarginMm - projectLogoOffsetXMm}mm;top:${projectSlotTopMm}mm;height:${projectSlotHeightMm}mm;display:flex;align-items:center;justify-content:flex-end;text-align:right;${projectHeaderTextCss}">${projectHeaderText}</div>`
        : `<img src="${String(projectLogoUrl || '')}" style="position:absolute;right:${pdfHeaderLogoRightMarginMm - projectLogoOffsetXMm}mm;top:${projectSlotTopMm}mm;height:${effectiveProjectLogoHeightMm}mm;object-fit:contain;" />`;
  const useFullWidthHeaderCenter =
    companyHeaderContentType === 'none' && projectHeaderContentType === 'none';
  const centerHeaderSlotHtml = headerCenterHtml
    ? useFullWidthHeaderCenter
      ? `<div style="position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);text-align:center;line-height:1.25;padding:0 2mm;box-sizing:border-box;z-index:3;">${headerCenterHtml}</div>`
      : `<div style="position:absolute;left:50%;top:50%;transform:translate(-50%, -50%);max-width:60%;text-align:center;line-height:1.25;z-index:3;">${headerCenterHtml}</div>`
    : '';
  const headerTemplate = `
    <div style="position:relative;width:100%;height:${headerHeightMm}mm;box-sizing:border-box;overflow:hidden;">
      ${companyHeaderSlotHtml}
      ${projectHeaderSlotHtml}
      ${centerHeaderSlotHtml}
      <div style="position:absolute;left:${pdfHeaderLogoLeftMarginMm}mm;right:${pdfHeaderLogoRightMarginMm}mm;bottom:0;border-bottom:1px solid #bbb;"></div>
    </div>
  `;
  const footerTemplate = showFooterContent ? `
    <div style="position:relative;width:100%;height:${footerHeightMm}mm;box-sizing:border-box;overflow:hidden;">
      <div style="position:absolute;left:${pdfHeaderLogoLeftMarginMm}mm;right:${pdfHeaderLogoRightMarginMm}mm;top:0;border-top:1px solid #bbb;"></div>
      <div style="position:absolute;left:${pdfHeaderLogoLeftMarginMm}mm;right:${pdfHeaderLogoRightMarginMm}mm;top:0;bottom:0;display:flex;align-items:center;justify-content:center;flex-direction:column;text-align:center;font-size:11px;line-height:1.35;color:#111;">
        <div>${footerAddressText}</div>
        <div>${footerPhoneText}${footerEmailText ? ` | ${footerEmailText}` : ''}</div>
      </div>
      ${
        showPageNumbers
          ? `<div style="position:absolute;right:${pdfHeaderLogoRightMarginMm}mm;bottom:1.2mm;font-size:10px;line-height:1;color:#444;z-index:3;">Page <span class="pageNumber"></span> of <span class="totalPages"></span></div>`
          : ''
      }
      ${
        footerOverlayShouldRender
          ? `<!--FOOTER_OVERLAY_START--><div class="doc-footer-overlay-layer" style="position:absolute;left:${pdfHeaderLogoLeftMarginMm}mm;right:${pdfHeaderLogoRightMarginMm}mm;top:0;bottom:0;display:flex;align-items:center;justify-content:center;text-align:center;font-size:${footerOverlayFontSizePx}px;pointer-events:none;transform:translate(${footerOverlayOffsetXMm}mm, ${footerOverlayOffsetYMm}mm);z-index:2;">${footerOverlayHtml}</div><!--FOOTER_OVERLAY_END-->`
          : ''
      }
    </div>
  ` : '<div></div>';
  const pdfHeaderSafeGapMm = 7;
  const pdfFooterSafeGapMm = 8;

  return {
    html: htmlForPdf,
    paperSize: String(paperSize || vm.paperSize || 'A4'),
    fileName,
    headerTemplate,
    footerTemplate,
    marginTopMm: Math.max(printPageTopMarginMm, headerHeightMm + pdfHeaderSafeGapMm),
    marginRightMm,
    marginBottomMm: Math.max(printPageBottomMarginMm, showFooterContent ? footerHeightMm + pdfFooterSafeGapMm : 0),
    marginLeftMm,
    footerOverlayPageScope,
    footerOverlayPageNumbers,
  };
};
